import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Chip,
  ListItemText,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Divider,
  Typography,
  ListItemButton,
  AppBar,
  IconButton,
  Toolbar,
  useMediaQuery,
  useTheme,
  Box,
  Pagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { extractApiErrorMessage, notifications } from '../utils';
import { useTranslation } from 'react-i18next';
import {
  AnimalTypeSelect,
  ClearFilters,
  Loader,
  LocationPositionSelect,
  LocationStableSelect,
  SearchIconButton
} from '.';
import { LoaderSize } from '../constants/constants';
import { LocationOption, LocationStable, toLocationOption } from '../models/locations';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { AnimalType } from '../models/animal';
import {
  HealthSystemDALEnumsAnimalType,
  HealthSystemDALEnumsLocationPosition,
  HealthSystemDALEnumsLocationStableType
} from '../api/HealthSystemApi';
import { ApiService } from '../hooks';

interface FarmLocationSelectModalProps {
  location: LocationOption | null;
  onChange: (location: LocationOption | null) => void;
  selectorId?: string;
  disableAny: boolean;
  disabled?: boolean;
  cssClass?: string;
  fullWidth?: boolean;
}

const FarmLocationSelectModal = React.forwardRef((props: FarmLocationSelectModalProps, ref) => {
  const { location, onChange, selectorId, disableAny, cssClass, disabled, fullWidth } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const apiService = new ApiService();

  const [locations, setLocations] = React.useState<LocationOption[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const [text, setText] = React.useState('');

  // location number text control
  const [number, setNumber] = React.useState('');

  const handleLocationNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumber(event.target.value);
  };
  // * location number text control
  // location room text control
  const [room, setRoom] = React.useState('');

  const handleLocationRoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoom(event.target.value);
  };
  // * location room text control
  // location pen text control
  const [pen, setPen] = React.useState('');

  const handleLocationPenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPen(event.target.value);
  };
  // * location pen text control

  React.useImperativeHandle(ref, () => ({
    openModal() {
      setOpen(true);
    }
  }));

  // pagination
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [animalType, setAnimalType] = React.useState<HealthSystemDALEnumsAnimalType>(0);
  const [locationPosition, setLocationPosition] = React.useState<HealthSystemDALEnumsLocationPosition | null>(null);
  const [locationStable, setLocationStable] = React.useState<HealthSystemDALEnumsLocationStableType>(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setIsLoaded(false);
  };
  // * pagination

  const locationDisplayCount = 10;

  const loadLocations = async () => {
    setIsBusy(true);

    apiService.api.locations
      .locationsSearchLocations({
        Page: page,
        Size: locationDisplayCount,
        Number: number,
        Room: room,
        Pen: pen,
        Position: locationPosition ? locationPosition : undefined,
        Stable: locationStable,
        AnimalType: animalType
      })
      .then((result) => {
        setLocations(result.data.data.map(toLocationOption));
        setTotal(result.data.total);
        setIsLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleSelectorClick = () => {
    setOpen(true);
    setIsLoaded(false);
  };

  React.useEffect(() => {
    if (open && !isLoaded) {
      loadLocations();
    }
  }, [open, isLoaded]);

  React.useEffect(() => {
    if (!location) {
      setTextAny();
    } else {
      setTextByLocation(location);
    }
  }, [location]);

  const handleClose = () => {
    clearFilters();
    setOpen(false);
  };

  const setTextByLocation = (location: LocationOption) => {
    setText(renderLocationFirstText(location));
  };

  const setTextAny = () => {
    setText(t('f-location-none'));
  };

  const handleLocationSelectClick = (location: LocationOption) => {
    setTextByLocation(location);
    setOpen(false);
    onChange(location);
  };

  const handleSelectAny = () => {
    setOpen(false);
    setTextAny();
    onChange(null);
  };

  const clearFilters = () => {
    setNumber('');
    setRoom('');
    setPen('');
    setAnimalType(0);
    setLocationPosition(null);
    setLocationStable(0);
    setPage(1);
  };

  const renderFiltersSection = () => {
    return (
      <Grid container direction="row" sx={{ mt: 2, mb: 1, pl: 1, pr: 1 }} spacing={1} size={{ xs: 12 }}>
        <Grid container direction="row" spacing={2}>
          <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
            <TextField
              label={t('f-chip-number')}
              variant="outlined"
              size="small"
              fullWidth
              value={number}
              onChange={handleLocationNumberChange}
            />
          </Grid>

          <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
            <TextField
              label={t('f-loc-room')}
              variant="outlined"
              size="small"
              fullWidth
              value={room}
              onChange={handleLocationRoomChange}
            />
          </Grid>

          <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
            <TextField
              label={t('f-loc-pen')}
              variant="outlined"
              size="small"
              fullWidth
              value={pen}
              onChange={handleLocationPenChange}
            />
          </Grid>
          <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
            <LocationPositionSelect
              value={locationPosition ?? 0}
              fullWidth
              allowAny
              onLocationPositionChange={(value) =>
                setLocationPosition(value === (0 as HealthSystemDALEnumsLocationPosition) ? null : value)
              }
            />
          </Grid>

          <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
            <LocationStableSelect
              value={locationStable}
              fullWidth
              allowAny
              onLocationStableChange={(value) => setLocationStable(value)}
            />
          </Grid>

          <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
            <AnimalTypeSelect
              value={animalType}
              fullWidth
              allowAny
              onAnimalTypeChange={(type) => setAnimalType(type)}
            />
          </Grid>

          <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
            <Box sx={{ alignItems: 'center', display: 'flex' }}>
              <ClearFilters onClick={clearFilters} />
              <SearchIconButton onClick={() => setIsLoaded(false)} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderLocationFirstText = (location: LocationOption) => {
    if (location === null) {
      return '';
    }

    let text = '';

    if (location.room !== null) text += t('f-loc-room') + ': ' + location.room;

    if (location.pen !== null) text += ' ' + t('f-loc-pen') + ': ' + location.pen;

    if (location.number !== null) text += ' ' + t('f-number') + ': ' + location.number;

    return text;
  };

  const renderLocationSecondaryText = (location: LocationOption) => {
    if (location === null) {
      return '';
    }

    let text = '';

    if (location.stable !== LocationStable.Unknown)
      text += t('f-loc-stable') + ': ' + LocationStable[location.stable].toString();

    if (location.animalType !== AnimalType.Unknown)
      text += ' / ' + t('f-com-animal-type') + ': ' + t(AnimalType[location.animalType].toString());

    return text;
  };

  return (
    <>
      <Button
        color="inherit"
        size="medium"
        key={selectorId || 'locationSelectModalBtn'}
        className={cssClass}
        fullWidth={fullWidth}
        variant="outlined"
        disabled={disabled}
        endIcon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
        onClick={handleSelectorClick}
      >
        {text}
      </Button>

      <Dialog
        onClose={handleClose}
        aria-labelledby="location-select-title"
        fullWidth
        maxWidth="md"
        open={open}
        fullScreen={fullScreen}
      >
        {fullScreen ? (
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ alignItems: 'center', display: 'flex' }}>
                  <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                  <Typography variant="h6">{t('f-location-select-mod')}</Typography>
                </Box>
              </Box>
            </Toolbar>
          </AppBar>
        ) : (
          <DialogTitle id="location-select-title">{t('f-location-select-mod')}</DialogTitle>
        )}

        <DialogContent sx={{ p: 1 }}>
          {renderFiltersSection()}

          <Divider />

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {isLoaded && (
            <Box sx={{ mt: 1, mr: 2, justifyContent: 'flex-end', display: 'flex' }}>
              <Typography>{t('f-search-results')}</Typography>
              <Box sx={{ ml: 1 }}>
                <Chip label={total} variant="outlined" size="small" id="totalChip" />
              </Box>
            </Box>
          )}

          {isLoaded && locations && (
            <List dense>
              {locations.map((location) => (
                <ListItemButton onClick={() => handleLocationSelectClick(location)} key={location.id}>
                  <ListItemAvatar>
                    <Avatar>{location.number?.[0]?.toUpperCase() || '-'}</Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={renderLocationFirstText(location)}
                    secondary={renderLocationSecondaryText(location)}
                  />
                </ListItemButton>
              ))}

              <ListItem id="pagination-li" sx={{ justifyContent: 'center' }}>
                <Pagination
                  count={Math.floor(total / locationDisplayCount)}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                  page={page}
                  onChange={handleChangePage}
                />
              </ListItem>
            </List>
          )}
        </DialogContent>

        <DialogActions>
          {!fullScreen && (
            <Button onClick={handleClose} color="primary">
              {t('m-global-close')}
            </Button>
          )}
          <Button onClick={handleSelectAny} color="primary" disabled={disableAny}>
            {t('f-location-none')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default FarmLocationSelectModal;
